import React, { useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { CSSTransition } from 'react-transition-group';

import ButtonWithEye from '../Button';
import { LineChartTitle } from './LineChartTitle';

import { LineChartWrapperProps } from '../../types/components';

import styles from './styles.module.scss';

export function LineChartWrapper({
  title,
  series,
  xAxis = {},
  legend,
  medianMinValue,
  medianMaxValue,
}: LineChartWrapperProps) {
  const [isLegendVisible, setIsLegendVisible] = useState<boolean>(true);

  const SERIES_BASE_CONFIG: Record<string, unknown> = {
    type: 'line',
    animation: true,
    hoverAnimation: true,
    smooth: true,
    smoothMonotone: 'x',
    showSymbol: true,
    symbol: 'circle',
    symbolSize: 8,
    emphasis: {
      focus: 'series',
      blurScope: 'coordinateSystem',
      scale: 2.5,
      itemStyle: {
        borderWidth: 4,
      },
    },
    itemStyle: {
      borderWidth: 2.2,
      borderColor: '#ffffff',
      shadowColor: 'rgba(50, 50, 71, 0.25)',
      shadowBlur: 5.5,
      shadowOffsetX: 0,
      shadowOffsetY: 2,
    },
    lineStyle: {
      width: 3.3,
      type: 'solid',
    },
    markLine: {
      silent: true,
      symbol: ['none', 'none'],
      label: { show: false },
      precision: 0,
      zlevel: 0,
      z: 0,
      lineStyle: {
        color: '#7a89a7',
        type: 'dashed',
        opacity: 0.5,
        width: 2.5,
      },
      data: [
        {
          yAxis: medianMaxValue,
        },
      ],
    },
  };

  const options = {
    tooltip: {
      trigger: 'item',
      hideDelay: 500,
      enterable: true,
      confine: false,
      appendToBody: true,
      padding: 0,
      position: 'left',
    },
    visualMap: {
      show: false,
      type: 'piecewise',
      pieces: [
        {
          max: medianMinValue - 0.01,
          color: '#ed5050',
        },
        {
          min: medianMinValue,
          max: medianMaxValue - 0.01,
          color: '#f9a31d',
        },
        {
          min: medianMaxValue,
          color: '#52b36b',
        },
      ],
    },
    grid: {
      containLabel: true,
      left: 0,
      top: 15,
      right: 35,
      bottom: 55,
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
      },
      {
        start: 0,
        end: 10,
      },
    ],
    xAxis: {
      ...xAxis,
      type: 'category',
      boundaryGap: false,
      splitLine: {
        show: false,
      },
      splitNumber: 2,
      axisLabel: {
        margin: 15,
        fontFamily: 'Open Sans',
        color: '#7a89a7',
        ...(xAxis?.axisLabel ?? {}),
      },
      axisLine: {
        lineStyle: {
          color: 'transparent',
        },
        ...(xAxis?.axisLine ?? {}),
      },
    },
    yAxis: {
      min: 0,
      max: 100,
      axisLabel: {
        margin: 15,
        color: '#7a89a7',
        fontFamily: 'Open Sans',
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: 'rgba(122, 137, 167, 0.3)',
          width: 1.2,
          type: 'dashed',
        },
      },
    },
    series: series.map((it) => ({
      ...SERIES_BASE_CONFIG,
      ...it,
      itemStyle: {
        ...(SERIES_BASE_CONFIG?.itemStyle ?? {}),
        ...(it?.itemStyle ?? {}),
      },
      lineStyle: {
        ...(SERIES_BASE_CONFIG?.lineStyle ?? {}),
        ...(it?.lineStyle ?? {}),
      },
    })),
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <LineChartTitle title={title} />
        <ButtonWithEye
          title={isLegendVisible ? 'Hide Legend' : 'Show Legend'}
          isOpen={isLegendVisible}
          onClick={() => setIsLegendVisible(!isLegendVisible)}
        />
      </div>
      <CSSTransition
        in={isLegendVisible}
        timeout={500}
        classNames="my-node"
        unmountOnExit
      >
        <div className={styles.legendWrapper}>{legend}</div>
      </CSSTransition>
      <div className={styles.contentWrapper}>
        <ReactECharts
          option={options}
          notMerge={true}
          lazyUpdate={true}
          style={{ height: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    </div>
  );
}
